import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Phone, Shield, GraduationCap, Zap, Sparkles, Briefcase } from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { api } from '../lib/api';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_730ca48e-6233-465b-bff6-fa986efd4fed/artifacts/gcnpy6sg_sacademie-logo-blanc-1024x547.png";
// Beige + navy variant — readable on the light login background.
const LOGO_BLUE = "https://customer-assets.emergentagent.com/job_730ca48e-6233-465b-bff6-fa986efd4fed/artifacts/9jfrbzlo_Sacademie-logo-bleu-1-1024x547.png";

// Dev convenience: quick-login credentials kept here and in
// /app/memory/test_credentials.md. Shown only when the env flag is true.
const QUICK_LOGINS = [
  {
    role: 'admin',
    email: 'admin@sellerie-academie.fr',
    password: 'admin123',
    label: 'Administrateur',
    sub: 'tableau de bord, FAQ, KB',
    Icon: Shield,
    accent: 'from-[#1F2A4A] to-[#324166]'
  },
  {
    role: 'eleve',
    email: 'eleve@sellerie-academie.fr',
    password: 'eleve123',
    label: 'Élève',
    sub: 'mes formations, parrainage',
    Icon: GraduationCap,
    accent: 'from-[#C9B896] to-[#E0D4BA]'
  },
  {
    role: 'apporteur',
    email: 'apporteur@sellerie-academie.fr',
    password: 'apporteur123',
    label: 'Apporteur',
    sub: 'candidats, commissions 10 %',
    Icon: Briefcase,
    accent: 'from-emerald-600 to-emerald-400'
  }
];

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLangPrefix();

  const from = location.state?.from?.pathname || withLangPrefix('/mes-formations', lang);

  const routeFor = (role) => {
    switch (role) {
      case 'admin': return withLangPrefix('/admin', lang);
      case 'gestionnaire': return withLangPrefix('/gestion', lang);
      case 'formateur': return withLangPrefix('/formateur', lang);
      case 'apporteur': return withLangPrefix('/apporteur', lang);
      default: return from;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.first_name} !`);
      navigate(routeFor(user.role));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (creds) => {
    setQuickLoading(creds.role);
    try {
      const user = await login(creds.email, creds.password);
      toast.success(`Connecté en tant que ${creds.label} — ${user.first_name}`);
      navigate(routeFor(user.role));
    } catch (error) {
      toast.error('Impossible de se connecter automatiquement');
    } finally {
      setQuickLoading(null);
    }
  };

  // Show quick login on every environment that is not strict production.
  // Easy to flip off later via REACT_APP_HIDE_QUICK_LOGIN.
  const showQuickLogin = process.env.REACT_APP_HIDE_QUICK_LOGIN !== 'true';

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_BLUE} alt="Sellerie Académie" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2" data-testid="login-title">
            Connexion
          </h1>
          <p className="text-muted-foreground">
            Accédez à votre espace personnel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="login-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold h-12 uppercase tracking-wide"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        {showQuickLogin && (
          <div className="mt-10" data-testid="quick-login-block">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px flex-1 bg-[#1F2A4A]/10" />
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896]">
                <Zap className="w-3 h-3" /> Connexion rapide
              </span>
              <span className="h-px flex-1 bg-[#1F2A4A]/10" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {QUICK_LOGINS.map((c) => {
                const Icon = c.Icon;
                const isLoading = quickLoading === c.role;
                const anyLoading = loading || quickLoading !== null;
                return (
                  <button
                    key={c.role}
                    type="button"
                    onClick={() => handleQuickLogin(c)}
                    disabled={anyLoading}
                    data-testid={`quick-login-${c.role}`}
                    className="group relative overflow-hidden border border-[#1F2A4A]/10 bg-white rounded-xl px-4 py-4 text-left transition-all duration-300 hover:border-[#C9B896] hover:shadow-[0_8px_24px_-12px_rgba(31,42,74,0.18)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500 pointer-events-none`} />
                    <div className="relative flex items-start gap-3">
                      <div className={`flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-sm`}>
                        <Icon className="w-4 h-4 text-white" strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F2A4A] leading-tight">
                          {isLoading ? 'Connexion…' : c.label}
                        </p>
                        <p className="text-[11px] text-[#4A5568] mt-0.5 leading-snug">
                          {c.sub}
                        </p>
                      </div>
                      <span className="text-[#C9B896] opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm">→</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[10px] font-mono tracking-[0.15em] uppercase text-[#1F2A4A]/40 text-center">
              comptes de démonstration · à supprimer en production
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-muted-foreground">
            Pas encore de compte ?{' '}
            <Link to={withLangPrefix('/inscription', lang)} className="text-[#C9B896] hover:underline" data-testid="login-to-register">
              S'inscrire
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLangPrefix();

  // Detect ?ref=CODE in URL and resolve referrer
  const refCode = new URLSearchParams(location.search).get('ref');
  useEffect(() => {
    if (refCode) {
      api.resolveReferral(refCode)
        .then((r) => setReferrer(r))
        .catch(() => setReferrer(null));
    }
  }, [refCode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...data } = formData;
      if (refCode) data.referred_by_code = refCode;
      await register(data);
      toast.success(referrer ? `Bienvenue ! ${referrer.first_name} vient de gagner 25 points.` : 'Compte créé avec succès !');
      navigate(withLangPrefix('/mes-formations', lang));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_BLUE} alt="Sellerie Académie" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2" data-testid="register-title">
            Créer un compte
          </h1>
          <p className="text-muted-foreground">
            Rejoignez Sellerie Académie
          </p>
          {referrer && (
            <div className="mt-5 mx-auto inline-flex items-center gap-2 px-4 py-2 bg-[#C9B896]/15 border border-[#C9B896]/40 rounded-full text-sm text-[#1F2A4A]" data-testid="referral-banner">
              <Sparkles className="w-3.5 h-3.5 text-[#C9B896]" />
              Parrainé par <strong className="font-semibold">{referrer.first_name} {referrer.last_name}</strong>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Prénom</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Prénom"
                  className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
                  required
                  data-testid="register-firstname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Nom</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Nom"
                className="bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="register-lastname"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="register-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone (optionnel)</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+33 6 12 34 56 78"
                className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
                data-testid="register-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
                required
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#C9B896] text-black hover:bg-[#E0D4BA] font-semibold h-12 uppercase tracking-wide"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? 'Création...' : 'Créer mon compte'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-muted-foreground">
            Déjà un compte ?{' '}
            <Link to={withLangPrefix('/connexion', lang)} className="text-[#C9B896] hover:underline" data-testid="register-to-login">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
